import React from "react";
import { HeadContent, Scripts, createRootRoute, useRouter } from "@tanstack/react-router";
import { TanStackRouterDevtoolsPanel } from "@tanstack/react-router-devtools";
import { TanStackDevtools } from "@tanstack/react-devtools";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Auth0Provider } from "@auth0/auth0-react";

import { queryClient, persistOptions } from "@/lib/query-client";
import { AuthProvider, useAuth } from "@/lib/auth/auth-context";
import { StudioProvider } from "@/lib/studio/studio-context";
import { CelebrationProvider } from "@/lib/celebration/celebration-context";
import type { RouterAuthContext } from "@/lib/auth/router-context";
import { auth0Config } from "@/lib/auth/auth0-config";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { SkipToContent } from "@/components/a11y/SkipToContent";
import { env, logEnvConfig } from "@/config/env";
import { initErrorTracking } from "@/lib/error-tracking";
import { initAnalytics } from "@/lib/analytics";
import { initPerformanceMonitoring, reportLongTasks } from "@/lib/performance";

import appCss from "@/styles.css?url";

// Initialize production services on app startup
if (!env.IS_TEST) {
  logEnvConfig();
  initErrorTracking();
  initAnalytics();

  // Initialize performance monitoring on client-side only
  if (typeof window !== 'undefined') {
    initPerformanceMonitoring();
    reportLongTasks();
  }
}

export const Route = createRootRoute({
  head: () => ({
    meta: [
      {
        charSet: "utf-8",
      },
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1",
      },
      {
        title: "AerialFlow - Studio Management & Training Platform",
      },
      {
        name: "description",
        content: "Modern platform for aerial studios to manage memberships, classes, and billing. Track your aerial training journey with comprehensive progress tracking.",
      },
      {
        name: "keywords",
        content: "aerial fitness, aerial silks, studio management, class scheduling, membership management, aerial training, circus arts",
      },
      {
        name: "author",
        content: "AerialFlow",
      },
      {
        property: "og:type",
        content: "website",
      },
      {
        property: "og:title",
        content: "AerialFlow - Studio Management & Training Platform",
      },
      {
        property: "og:description",
        content: "Modern platform for aerial studios to manage memberships, classes, and billing. Track your aerial training journey.",
      },
      {
        property: "og:site_name",
        content: "AerialFlow",
      },
      {
        name: "twitter:card",
        content: "summary_large_image",
      },
      {
        name: "twitter:title",
        content: "AerialFlow - Studio Management & Training Platform",
      },
      {
        name: "twitter:description",
        content: "Modern platform for aerial studios to manage memberships, classes, and billing.",
      },
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
    ],
    scripts: env.ENABLE_ANALYTICS && env.ANALYTICS_DOMAIN
      ? [
          {
            src: "https://plausible.io/js/script.js",
            defer: true,
            'data-domain': env.ANALYTICS_DOMAIN,
          },
        ]
      : [],
  }),

  shellComponent: RootDocument,
});

/**
 * Inner component that hydrates router context with auth state
 * This bridges React Context (useAuth) to Router Context
 */
function RouterContextProvider({ children }: { children: React.ReactNode }) {
  const auth = useAuth()
  const router = useRouter()

  // Update router context when auth state changes
  React.useEffect(() => {
    const newContext: RouterAuthContext = {
      session: auth.session,
      isLoading: auth.isLoading || auth.isSessionLoading,
      isAuthenticated: auth.isAuthenticated,
    }

    // Only update if context actually changed to avoid infinite loops
    const currentContext = router.options.context as RouterAuthContext
    if (
      currentContext.session !== newContext.session ||
      currentContext.isLoading !== newContext.isLoading ||
      currentContext.isAuthenticated !== newContext.isAuthenticated
    ) {
      router.update({
        context: newContext,
      })
    }
  }, [
    auth.session,
    auth.isLoading,
    auth.isSessionLoading,
    auth.isAuthenticated,
    router,
  ])

  // Wrap children with StudioProvider for permission context
  // CelebrationProvider inside StudioProvider to access studio settings
  return (
    <StudioProvider session={auth.session} getAccessToken={auth.getAccessToken}>
      <CelebrationProvider>
        {children}
      </CelebrationProvider>
    </StudioProvider>
  )
}

function RootDocument({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        <SkipToContent />
        <ErrorBoundary level="root">
          <Auth0Provider {...auth0Config}>
            <PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>
              <AuthProvider>
                <RouterContextProvider>
                  <div id="main-content" className="min-h-screen">
                    {children}
                  </div>
                {env.ENABLE_DEVTOOLS && (
                  <TanStackDevtools
                    config={{
                      position: "bottom-right",
                    }}
                    plugins={[
                      {
                        name: "Tanstack Router",
                        render: <TanStackRouterDevtoolsPanel />,
                      },
                      {
                        name: "React Query",
                        render: <ReactQueryDevtools />,
                      },
                    ]}
                  />
                )}
                <Scripts />
                </RouterContextProvider>
              </AuthProvider>
            </PersistQueryClientProvider>
          </Auth0Provider>
        </ErrorBoundary>
        </body>
    </html>
  );
}
